import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalLocation } from '@/contexts/LocationContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MapPin } from 'lucide-react';
import api from '@/lib/api';
import { ProbleemMeldenDialog } from '@/components/ProbleemMeldenDialog';
import {
  LayoutDashboard,
  Users,
  CalendarCheck,
  CalendarDays,
  Grid3X3,
  Clock4,
  ClipboardList,
  ClipboardCheck,
  Euro,
  CalendarRange,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  Bell,
  FileSignature,
  Gauge,
  FileUp,
  CalendarClock,
  Briefcase,
  Bug,
  BookOpen,
} from 'lucide-react';

const NAV_GROUPS = [
  {
    name: 'Overzicht',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['owner', 'admin', 'multi_location'] },
      { to: '/locaties', label: 'Dashboard', icon: LayoutDashboard, roles: ['single_location'] },
      { to: '/locaties', label: 'Locaties', icon: MapPin, roles: ['owner', 'admin', 'multi_location'] },
    ]
  },
  {
    name: 'Plannen',
    items: [
      { to: '/rooster', label: 'Roosterplanner', icon: CalendarDays, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/standaardrooster', label: 'Standaardrooster', icon: Grid3X3, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/beschikbaarheid', label: 'Beschikbaarheid', icon: CalendarCheck, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/weekoverzicht', label: 'Weekoverzicht', icon: ClipboardCheck, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
    ]
  },
  {
    name: 'Uren & klok',
    items: [
      { to: '/clock', label: 'Inklokken', icon: Clock4, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/uren-overzicht', label: 'Uren Overzicht', icon: ClipboardList, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/goedkeuringen', label: 'Uren goedkeuren', icon: ClipboardCheck, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/uren-import', label: 'Uren-import', icon: FileUp, roles: ['owner', 'admin'] },
    ]
  },
  {
    name: 'Personeel',
    items: [
      { to: '/medewerkers', label: 'Medewerkers', icon: Users, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/vacatures', label: 'Vacatures', icon: Briefcase, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/contracten', label: 'Contracten', icon: FileSignature, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/instructiebladen', label: 'Instructiebladen', icon: BookOpen, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
    ]
  },
  {
    name: 'Verloning',
    items: [
      { to: '/verloning', label: 'Verloning', icon: Euro, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/verlofaanvragen', label: 'Verlofaanvragen', icon: CalendarClock, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
      { to: '/loonperiode', label: 'Loonperiode', icon: CalendarRange, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
    ]
  },
  {
    name: 'AI & systeem',
    items: [
      { to: '/ai-usage', label: 'AI Gebruik', icon: ShieldCheck, roles: ['owner', 'admin'] },
      { to: '/ai-health', label: 'AI Gezondheid', icon: Gauge, roles: ['owner', 'admin'] },
      { to: '/probleemmeldingen', label: 'Probleemmeldingen', icon: Bug, roles: ['owner', 'admin'] },
      { to: '/instellingen', label: 'Instellingen', icon: Settings, roles: ['owner', 'admin'] },
      { to: '/audit-log', label: 'Audit log', icon: ShieldCheck, roles: ['owner', 'admin', 'multi_location', 'single_location'] },
    ]
  }
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [docAttention, setDocAttention] = useState(0);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [bugNieuw, setBugNieuw] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { globalLocation, setGlobalLocation } = useGlobalLocation();
  const [locationsList, setLocationsList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchLocations = async () => {
      try {
        const { data } = await api.get('/locations');
        if (!cancelled) setLocationsList(data);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('locations fetch failed', e);
      }
    };
    fetchLocations();
    return () => { cancelled = true; };
  }, []);

  // Collapsible groups state
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('tw1_sidebar_expanded');
      return saved ? JSON.parse(saved) : NAV_GROUPS.map(g => g.name);
    } catch {
      return NAV_GROUPS.map(g => g.name);
    }
  });

  const showDocAttention = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/notifications/unread-count');
        if (!cancelled) setUnreadCount(data.unread || 0);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('[appshell] unread fetch failed', e);
      }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname]);

  useEffect(() => {
    if (!showDocAttention) return undefined;
    let cancelled = false;
    const fetchDocs = async () => {
      try {
        const { data } = await api.get('/documents/expiring', { params: { days: 30 } });
        if (!cancelled) setDocAttention(data.count || 0);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('[appshell] doc-attention fetch failed', e);
      }
    };
    fetchDocs();
    const t = setInterval(fetchDocs, 5 * 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [showDocAttention, location.pathname]);

  const showLeaveBadge = ['owner', 'admin', 'multi_location', 'single_location'].includes(user?.role);
  useEffect(() => {
    if (!showLeaveBadge) return undefined;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const { data } = await api.get('/leave/pending');
        if (!cancelled) setPendingLeave((data.records || []).length);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('[appshell] pending-leave fetch failed', e);
      }
    };
    fetchPending();
    const t = setInterval(fetchPending, 5 * 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [showLeaveBadge, location.pathname]);

  const showBugBadge = user?.role === 'owner' || user?.role === 'admin';
  useEffect(() => {
    if (!showBugBadge) return undefined;
    let cancelled = false;
    const fetchBug = async () => {
      try {
        const { data } = await api.get('/bug-reports/nieuw-count');
        if (!cancelled) setBugNieuw(data.nieuw || 0);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('[appshell] bug-nieuw fetch failed', e);
      }
    };
    fetchBug();
    const t = setInterval(fetchBug, 5 * 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [showBugBadge, location.pathname]);

  // Ensure active group stays expanded
  useEffect(() => {
    const activeGroup = NAV_GROUPS.find(g => g.items.some(i => location.pathname.startsWith(i.to)));
    if (activeGroup && !expandedGroups.includes(activeGroup.name)) {
      setExpandedGroups(prev => {
        const next = [...prev, activeGroup.name];
        localStorage.setItem('tw1_sidebar_expanded', JSON.stringify(next));
        return next;
      });
    }
  }, [location.pathname, expandedGroups]);

  const toggleGroup = (name) => {
    if (collapsed) {
      setCollapsed(false);
      setExpandedGroups(prev => {
        const next = prev.includes(name) ? prev : [...prev, name];
        localStorage.setItem('tw1_sidebar_expanded', JSON.stringify(next));
        return next;
      });
      return;
    }
    setExpandedGroups(prev => {
      const isExpanded = prev.includes(name);
      const next = isExpanded ? prev.filter(g => g !== name) : [...prev, name];
      localStorage.setItem('tw1_sidebar_expanded', JSON.stringify(next));
      return next;
    });
  };

  const getPageTitle = () => {
    // Exact match for cockpit
    if (location.pathname.startsWith('/locaties/')) {
       const slug = location.pathname.replace('/locaties/', '');
       const found = locationsList.find(l => l.slug === slug);
       return found ? found.name : 'Locatie Cockpit';
    }
    for (const group of NAV_GROUPS) {
      const active = group.items.find(n => location.pathname.startsWith(n.to));
      if (active) return active.label;
    }
    return 'SmartRoster';
  };
  const canChangeLocation = user && (user.role === 'owner' || user.role === 'admin' || user.role === 'multi_location');
  const selectedLocName = globalLocation === 'all' 
    ? 'Alle locaties' 
    : locationsList.find(l => l.slug === globalLocation)?.name || globalLocation;

  return (
    <div className="flex h-screen bg-tw1-main-bg font-sans text-slate-900">
      <Helmet><title>SmartRoster · Truckwash1</title></Helmet>
      <aside
        className={`flex flex-col bg-gradient-to-b from-tw1-brand-navy-top to-tw1-brand-navy transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <div className="flex items-center justify-end px-4 py-4 border-b border-white/5">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="rounded-md p-1.5 text-tw1-sidebar-text hover:bg-tw1-brand-navy-hover hover:text-white transition-colors"
            data-testid="sidebar-collapse"
            aria-label="Sidebar in/uitklappen"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter(item => !item.roles || item.roles.includes(user?.role));
            if (visibleItems.length === 0) return null;

            const isExpanded = expandedGroups.includes(group.name);

            return (
              <div key={group.name} className="space-y-1">
                {!collapsed && (
                  <button
                    onClick={() => toggleGroup(group.name)}
                    className="flex w-full items-center justify-between px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-tw1-sidebar-label hover:text-tw1-sidebar-text transition-colors"
                  >
                    <span>{group.name}</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
                    />
                  </button>
                )}
                
                {(isExpanded || collapsed) && (
                  <div className="space-y-0.5">
                    {visibleItems.map(({ to, label, icon: Icon }) => {
                      const isInstellingen = to === '/instellingen';
                      const showDot = isInstellingen && showDocAttention && docAttention > 0;
                      const isVerlof = to === '/verlofaanvragen';
                      const showLeaveDot = isVerlof && showLeaveBadge && pendingLeave > 0;
                      const isProbleem = to === '/probleemmeldingen';
                      const showBugDot = isProbleem && showBugBadge && bugNieuw > 0;
                      const dotCount = isVerlof ? pendingLeave : (isProbleem ? bugNieuw : docAttention);
                      const anyDot = showDot || showLeaveDot || showBugDot;
                      return (
                        <NavLink
                          key={to}
                          to={to}
                          data-testid={`nav-${to.replace('/', '')}`}
                          title={collapsed ? label : (showDot ? `${docAttention} document(en) vereisen aandacht` : (showLeaveDot ? `${pendingLeave} openstaande verlofaanvra(a)g(en)` : undefined))}
                          className={({ isActive }) =>
                            `relative flex items-center gap-3 rounded-r-md px-3 py-2 text-[13px] font-medium transition-colors border-l-[3px] ${
                              isActive
                                ? 'border-tw1-accent-gold bg-tw1-sidebar-active-bg text-tw1-sidebar-active-text'
                                : 'border-transparent text-tw1-sidebar-text hover:bg-tw1-brand-navy-hover hover:text-white'
                            } ${collapsed ? 'justify-center px-0' : ''}`
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <span className="relative shrink-0">
                                <Icon size={18} className={`shrink-0 ${isActive ? 'text-tw1-accent-gold' : ''}`} />
                                {anyDot && (
                                  <span
                                    data-testid={isVerlof ? 'nav-verlofaanvragen-pending-dot' : (isProbleem ? 'nav-probleemmeldingen-nieuw-dot' : 'nav-instellingen-doc-dot')}
                                    className="absolute -top-1 -right-1 flex h-3 min-w-[12px] items-center justify-center rounded-full bg-tw1-status-danger px-1 text-[8px] font-bold text-white ring-2 ring-tw1-sidebar-bg"
                                  >
                                    {dotCount > 9 ? '9+' : dotCount}
                                  </span>
                                )}
                              </span>
                              {!collapsed && <span>{label}</span>}
                            </>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/5 p-4">
          {!collapsed && user && (
            <button
              type="button"
              onClick={() => navigate('/mijn-account')}
              data-testid="sidebar-my-account"
              title="Mijn account"
              className="mb-3 w-full rounded-md px-2 py-1.5 -mx-2 text-left text-xs leading-tight hover:bg-tw1-brand-navy-hover transition-colors"
            >
              <p className="font-semibold text-white">{user.name}</p>
              <p className="text-tw1-sidebar-label truncate">{user.email}</p>
              <p className="mt-1.5 inline-block rounded bg-tw1-brand-navy px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-tw1-sidebar-text">
                {user.role}
              </p>
            </button>
          )}
          <button
            type="button"
            onClick={logout}
            data-testid="logout-button"
            title={collapsed ? "Uitloggen" : undefined}
            className={`flex w-full items-center justify-center gap-2 rounded-md bg-transparent px-3 py-2 text-xs font-medium text-tw1-sidebar-text hover:bg-tw1-brand-navy-hover hover:text-white transition-colors ${collapsed ? 'px-0' : ''}`}
          >
            <LogOut size={16} />
            {!collapsed && <span>Uitloggen</span>}
          </button>
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-tw1-brand-navy-hover bg-gradient-to-r from-tw1-brand-navy-top to-tw1-brand-navy px-8 shadow-sm">
          <div className="flex items-center gap-4">
            <img 
              src="/images/logo.png" 
              alt="Truckwash1" 
              className="h-8 w-auto object-contain"
            />
            <div className="h-6 w-px bg-white/20" />
            <span className="text-lg font-bold tracking-tight text-white">
              SmartRoster
            </span>
            <div className="h-4 w-px bg-white/10 ml-1 mr-1" />
            <h1 className="text-sm font-semibold tracking-wide text-white/60 uppercase">
              {getPageTitle()}
            </h1>
          </div>
          <div className="flex items-center gap-4 text-sm text-white/90">
            {/* Header Location Selector */}
            {canChangeLocation ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 hover:bg-white/20 transition-colors mr-2">
                    <MapPin size={14} className="text-tw1-accent-gold" />
                    <span className="text-xs font-semibold tracking-wide text-white">
                      Locatie: {selectedLocName} <ChevronDown size={12} className="inline ml-1 opacity-70" />
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white text-slate-900 border-[#e4e9f0]">
                  <DropdownMenuItem 
                    onClick={() => {
                      setGlobalLocation('all');
                      navigate('/locaties');
                    }}
                    className={`text-sm cursor-pointer ${globalLocation === 'all' ? 'bg-slate-50 font-bold text-tw1-brand-navy' : ''}`}
                  >
                    Alle locaties
                  </DropdownMenuItem>
                  {locationsList.map((loc) => (
                    <DropdownMenuItem 
                      key={loc.slug}
                      onClick={() => {
                        setGlobalLocation(loc.slug);
                        navigate(`/locaties/${loc.slug}`);
                      }}
                      className={`text-sm cursor-pointer ${globalLocation === loc.slug ? 'bg-slate-50 font-bold text-tw1-brand-navy' : ''}`}
                    >
                      {loc.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 mr-2">
                <MapPin size={14} className="text-tw1-accent-gold" />
                <span className="text-xs font-semibold tracking-wide text-white">
                  Locatie: {selectedLocName}
                </span>
              </div>
            )}

            <span data-testid="current-date-nl" className="font-medium">
              {new Date().toLocaleDateString('nl-NL', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <Link
              to="/meldingen"
              data-testid="header-notifications-btn"
              className="relative flex h-9 w-9 items-center justify-center rounded-md text-white/80 hover:bg-white/10 hover:text-white transition-colors ml-2"
              aria-label="Meldingen"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span
                  data-testid="notifications-badge-count"
                  className="absolute 0 right-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-tw1-status-danger px-1 text-[10px] font-semibold text-white border border-tw1-brand-navy"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
            <ProbleemMeldenDialog compact locationScope={globalLocation} />
            {user && (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-tw1-accent-gold text-tw1-brand-navy font-bold text-sm shadow-sm ml-2">
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-8">{children}</div>
      </main>
    </div>
  );
}
